import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { App } from 'antd';
import { useNovu, type Notification } from '@novu/react';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from '@/translations';
import { useComms } from '../comms/context/CommsContext';
import { useConversationDrawer } from '../comms/context/ConversationDrawerContext';
import { useCall } from '../comms/call/CallContext';
import { decideNotification } from './policy';
import type {
  AppNotificationCandidate,
  AppNotificationSettings,
  MatrixCallNotificationCandidate,
  NovuNotificationCandidate,
} from './model';
import {
  createDesktopNotificationChannel,
  type DesktopNotificationChannel,
} from './desktop';
import {
  createNotificationAudioChannel,
  type NotificationAudioChannel,
} from './audio';
import {
  createNotificationTabCoordinator,
  type NotificationTabCoordinator,
} from './tabCoordinator';
import './AppNotificationHost.scss';

interface InAppNotificationInput {
  key: string;
  message: React.ReactNode;
  description?: React.ReactNode;
  duration: number;
  placement: 'topRight';
  className: string;
  style: React.CSSProperties;
  onClick(): void;
  onClose(): void;
}

export interface InAppNotificationChannel {
  open(input: InAppNotificationInput): void;
  destroy(key?: string): void;
}

interface AppNotificationHostPorts {
  notification?: InAppNotificationChannel;
  desktop?: DesktopNotificationChannel;
  audio?: NotificationAudioChannel;
  coordinator?: NotificationTabCoordinator;
  getPageState?: () => { isVisible: boolean; hasFocus: boolean };
  focusWindow?: () => void;
  now?: () => number;
  setTimeout?: NotificationSetTimeout;
  clearTimeout?: NotificationClearTimeout;
}

interface AppNotificationHostProps {
  callerSubject: string;
  settings: AppNotificationSettings;
  openNotifications(): void;
  ports?: AppNotificationHostPorts;
}

interface GroupedCandidate {
  count: number;
  lastReceivedAt: number;
}

type NotificationTimer = ReturnType<typeof globalThis.setTimeout>;
type NotificationSetTimeout = (
  callback: () => void,
  delay: number,
) => NotificationTimer;
type NotificationClearTimeout = (timer: NotificationTimer) => void;

const aggregateMilliseconds = 10_000;
const cardDurationMilliseconds = 5_000;
const maxCards = 3;

const safelyRun = (action: () => void) => {
  try {
    action();
  } catch {
    return;
  }
};

const validNovuTarget = (target: string | undefined) => {
  if (!target) return null;
  try {
    const url = new URL(target, `${window.location.origin}/`);
    if (url.origin !== window.location.origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

const toNovuCandidate = (
  notification: Notification,
  receivedAt: number,
): NovuNotificationCandidate => {
  const workflowId =
    notification.workflow?.identifier ??
    notification.workflow?.id ??
    notification.id;
  return {
    source: 'novu',
    id: notification.id,
    groupKey: `novu:${workflowId}`,
    title:
      notification.subject ?? notification.workflow?.name ?? 'Notification',
    preview: notification.body,
    receivedAt,
    severity: notification.severity ?? notification.workflow?.severity,
    workflowId,
    workflowCritical: notification.workflow?.critical ?? false,
    tags: [
      ...(notification.tags ?? []),
      ...(notification.workflow?.tags ?? []),
    ],
    target: notification.redirect?.url,
  };
};

export const AppNotificationHost = ({
  callerSubject,
  settings,
  openNotifications,
  ports,
}: AppNotificationHostProps) => {
  const { notification: appNotification } = App.useApp();
  const novu = useNovu();
  const navigate = useNavigate();
  const { t } = useTranslate();
  const { conversations, subscribeIncomingMessages } = useComms();
  const drawer = useConversationDrawer();
  const call = useCall();
  const defaultDesktop = useMemo(createDesktopNotificationChannel, []);
  const defaultAudio = useMemo(createNotificationAudioChannel, []);
  const defaultCoordinator = useMemo(
    () => createNotificationTabCoordinator(callerSubject),
    [callerSubject],
  );
  const defaultGetPageState = useCallback(
    () => ({
      isVisible: document.visibilityState === 'visible',
      hasFocus: document.hasFocus(),
    }),
    [],
  );
  const defaultFocusWindow = useCallback(() => window.focus(), []);
  const notification = ports?.notification ?? appNotification;
  const desktop = ports?.desktop ?? defaultDesktop;
  const audio = ports?.audio ?? defaultAudio;
  const coordinator = ports?.coordinator ?? defaultCoordinator;
  const getPageState = ports?.getPageState ?? defaultGetPageState;
  const focusWindow = ports?.focusWindow ?? defaultFocusWindow;
  const now = ports?.now ?? Date.now;
  const setTimeoutFn = ports?.setTimeout ?? globalThis.setTimeout;
  const clearTimeoutFn = ports?.clearTimeout ?? globalThis.clearTimeout;
  const groupsRef = useRef(new Map<string, GroupedCandidate>());
  const activeCardsRef = useRef(new Map<string, NotificationTimer>());
  const lastPresentedCallRef = useRef<string | null>(null);
  const [coordinatorRevision, setCoordinatorRevision] = useState(0);

  const activeRoomId = useMemo(() => {
    if (!drawer.isOpen || !drawer.target) return null;
    return (
      conversations.find(
        (conversation) => conversation.operatorId === drawer.target?.userId,
      )?.roomId ?? null
    );
  }, [conversations, drawer.isOpen, drawer.target]);

  const openCandidate = useCallback(
    (candidate: AppNotificationCandidate) => {
      if (candidate.source === 'novu') {
        const target = validNovuTarget(candidate.target);
        if (target) navigate(target);
        else openNotifications();
        return;
      }
      safelyRun(focusWindow);
      drawer.openConversation({
        userId: candidate.operatorId,
        displayName: candidate.title,
      });
    },
    [drawer, focusWindow, navigate, openNotifications],
  );

  const clearActiveCard = useCallback(
    (key: string) => {
      const timer = activeCardsRef.current.get(key);
      if (timer) clearTimeoutFn(timer);
      activeCardsRef.current.delete(key);
    },
    [clearTimeoutFn],
  );

  const presentCandidate = useCallback(
    (candidate: AppNotificationCandidate) => {
      if (!coordinator.isPresentationLeader()) return false;
      const isDuplicate =
        candidate.source !== 'matrix-call' &&
        coordinator.hasHandled(candidate.id);
      const pageState = getPageState();
      const decision = decideNotification(candidate, {
        ...pageState,
        activeRoomId,
        desktopAvailable: desktop.permission() === 'granted',
        isDuplicate,
        settings,
      });
      if (decision.channel === 'silent') return false;
      if (candidate.source !== 'matrix-call')
        coordinator.markHandled(candidate.id);

      const receivedAt = now();
      groupsRef.current.forEach((group, key) => {
        if (receivedAt - group.lastReceivedAt > aggregateMilliseconds)
          groupsRef.current.delete(key);
      });
      const existingGroup = groupsRef.current.get(candidate.groupKey);
      const group = {
        count:
          existingGroup &&
          receivedAt - existingGroup.lastReceivedAt <= aggregateMilliseconds
            ? existingGroup.count + 1
            : 1,
        lastReceivedAt: receivedAt,
      };
      groupsRef.current.set(candidate.groupKey, group);
      const title =
        group.count > 1
          ? `${candidate.title} (${group.count})`
          : candidate.title;
      const onOpen = () => openCandidate(candidate);

      if (decision.channel === 'desktop') {
        desktop.show({
          title,
          groupKey: candidate.groupKey,
          preview: candidate.preview,
          showPreview: settings.showPreviews,
          onOpen,
        });
      } else {
        const isActive = activeCardsRef.current.has(candidate.groupKey);
        if (isActive || activeCardsRef.current.size < maxCards) {
          clearActiveCard(candidate.groupKey);
          const right = drawer.isOpen ? 452 : 16;
          const timer = setTimeoutFn(
            () => activeCardsRef.current.delete(candidate.groupKey),
            cardDurationMilliseconds,
          );
          activeCardsRef.current.set(candidate.groupKey, timer);
          try {
            notification.open({
              key: candidate.groupKey,
              message: title,
              description: candidate.preview,
              duration: cardDurationMilliseconds / 1_000,
              placement: 'topRight',
              className: 'app-notification-card',
              style: {
                width: `min(384px, calc(100vw - ${right + 16}px))`,
                marginRight: drawer.isOpen ? 436 : 0,
              },
              onClick: () => {
                onOpen();
                notification.destroy(candidate.groupKey);
                clearActiveCard(candidate.groupKey);
              },
              onClose: () => clearActiveCard(candidate.groupKey),
            });
          } catch {
            clearActiveCard(candidate.groupKey);
          }
        }
      }

      if (decision.sound === 'chime') void audio.playChime();
      return true;
    },
    [
      activeRoomId,
      audio,
      clearActiveCard,
      coordinator,
      desktop,
      drawer.isOpen,
      getPageState,
      notification,
      now,
      openCandidate,
      setTimeoutFn,
      settings,
    ],
  );

  const callCandidate = useMemo<MatrixCallNotificationCandidate | null>(() => {
    if (
      call.status !== 'ringing' ||
      call.direction !== 'incoming' ||
      !call.roomId ||
      call.operatorId == null
    )
      return null;
    const conversation = conversations.find(
      (item) => item.operatorId === call.operatorId,
    );
    const displayName =
      conversation?.displayName ?? `Operator ${call.operatorId}`;
    return {
      source: 'matrix-call',
      id: `matrix-call:${call.roomId}`,
      groupKey: `matrix:${call.roomId}`,
      title: t('notificationIncomingCall', { name: displayName }),
      receivedAt: now(),
      roomId: call.roomId,
      operatorId: call.operatorId,
    };
  }, [call, conversations, now, t]);

  useEffect(() => {
    coordinator.start();
    const unsubscribe = coordinator.subscribe(() =>
      setCoordinatorRevision((revision) => revision + 1),
    );
    return () => {
      unsubscribe();
      coordinator.stop();
    };
  }, [coordinator]);

  useEffect(() => {
    let disposed = false;
    const unsubscribe = novu.on(
      'notifications.notification_received',
      ({ result }) => {
        if (!coordinator.isPresentationLeader()) return;
        void (async () => {
          let notificationResult = result;
          const severity =
            notificationResult.severity ??
            notificationResult.workflow?.severity;
          if (!severity) {
            try {
              const { data } = await novu.notifications.list({
                limit: 100,
                useCache: false,
              });
              const hydrated = data?.notifications.find(
                (notification) => notification.id === result.id,
              );
              if (!hydrated) return;
              notificationResult = hydrated;
            } catch {
              return;
            }
          }
          if (disposed) return;
          presentCandidate(toNovuCandidate(notificationResult, now()));
        })();
      },
    );
    return () => {
      disposed = true;
      unsubscribe();
    };
  }, [coordinator, novu, now, presentCandidate]);

  useEffect(
    () =>
      subscribeIncomingMessages((event) => {
        const conversation = conversations.find(
          (item) => item.roomId === event.roomId,
        );
        if (!conversation) return;
        const title =
          conversation.displayName ?? `Operator ${conversation.operatorId}`;
        const preview =
          event.message.kind === 'image'
            ? t('commsImageMessage')
            : event.message.kind === 'voice'
              ? t('commsVoiceMessage')
              : event.message.body;
        presentCandidate({
          source: 'matrix-message',
          id: event.eventId,
          groupKey: `matrix:${event.roomId}`,
          title,
          preview,
          receivedAt: now(),
          roomId: event.roomId,
          operatorId: conversation.operatorId,
          messageKind: event.message.kind,
        });
      }),
    [conversations, now, presentCandidate, subscribeIncomingMessages, t],
  );

  useEffect(() => {
    if (!callCandidate) {
      lastPresentedCallRef.current = null;
      return;
    }
    if (lastPresentedCallRef.current === callCandidate.id) return;
    if (presentCandidate(callCandidate))
      lastPresentedCallRef.current = callCandidate.id;
  }, [callCandidate, coordinatorRevision, presentCandidate]);

  useEffect(() => {
    if (!callCandidate || !coordinator.isPresentationLeader()) {
      audio.stopRinging();
      return;
    }
    const pageState = getPageState();
    const decision = decideNotification(callCandidate, {
      ...pageState,
      activeRoomId,
      desktopAvailable: desktop.permission() === 'granted',
      isDuplicate: false,
      settings,
    });
    if (decision.sound === 'ring' && decision.channel !== 'silent')
      void audio.startRinging();
    else audio.stopRinging();
  }, [
    activeRoomId,
    audio,
    callCandidate,
    coordinator,
    coordinatorRevision,
    desktop,
    getPageState,
    settings,
  ]);

  useEffect(
    () => () => {
      activeCardsRef.current.forEach((timer, key) => {
        clearTimeoutFn(timer);
        notification.destroy(key);
      });
      activeCardsRef.current.clear();
      groupsRef.current.clear();
      audio.stopRinging();
      audio.dispose();
    },
    [audio, clearTimeoutFn, notification],
  );

  return null;
};
